import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import { StorageClient } from '@atlaskit/frontend-utilities/storage-client';
import { VARIANT_ORDER } from '../../common/constants';
import { NO_OP, remoteImport } from '../../common/utils';
import { PluginName } from '../../types';
import generateHash from '../generate-hash';
import { setupSystemModules } from '../remote-setup';
import { getUfoExperience } from '../use-remote-module-loader/utils';
const isRemoteModuleConfig = config => {
  return (config === null || config === void 0 ? void 0 : config.moduleUri) !== undefined;
};
class AtlassianSystemRemoteFrontendSdk {
  constructor(plugin) {
    // list of remote modules to be imported
    // map of the above modules
    // map of the remote modules that have been fetched
    _defineProperty(this, "resolvedModuleMap", new Map());
    _defineProperty(this, "storageClient", new StorageClient(`remote-module-loader-bifrost-override`, {
      storageEngine: 'sessionStorage'
    }));
    this.plugin = plugin;
    this.importMap = this.findPluginManifest(plugin);
    this.setImportMaps([this.importMap]);
    this.renderUfoExperience = getUfoExperience(this.getModuleName(), 'render');
  }

  /**
   * Using the dimensions defined in the plugin, search through the manifest and find the most applicable one
   *
   * @param plugin
   * @private
   */
  findPluginManifest(plugin) {
    if (plugin) {
      const {
        manifestMap,
        dimensions
      } = plugin;
      const manifestEnv = manifestMap.get(dimensions.env);
      if (manifestEnv) {
        // This is not currently allowed in the types but is an escape hatch for not defining any variants
        if (isRemoteModuleConfig(manifestEnv)) {
          return manifestEnv;
        }

        /**
         * If we request a `latest` dimension, but only have `stable` in our map then we use it.
         * If we request a `stable` dimension, but only have `latest` an error will be thrown due to the variant order.
         */
        const variantIndex = VARIANT_ORDER.indexOf(dimensions.variant);
        if (variantIndex > -1) {
          for (let i = variantIndex; i < VARIANT_ORDER.length; i++) {
            const manifestVariant = manifestEnv.get(VARIANT_ORDER[i]);
            if (isRemoteModuleConfig(manifestVariant)) {
              this.variant = VARIANT_ORDER[i]; // set the variant applied for use later

              return manifestVariant;
            }
          }
          throw new Error(`No valid "variant" dimensions defined in plugin for [${dimensions.variant}].`);
        }
        throw new Error(`Invalid "variant" dimension defined in plugin for [${dimensions.variant}].`);
      }
      throw new Error(`No "env" dimension defined in plugin for [${dimensions.env}].`);
    }
    throw new Error('Invalid "plugin" provided to SDK.');
  }
  getModuleName(importMap = this.importMap) {
    if (importMap) {
      return importMap.moduleName;
    }
    return '';
  }
  loadFromQueryAndSaveToSession(key) {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has(`${key}-clear`)) {
        this.storageClient.removeItem(key);
        return undefined;
      }
      if (params.has(key)) {
        let value = params.get(key);
        this.storageClient.setItemWithExpiry(key, value, 3 * 3600 * 1000); // 3 hours in ms
        return value;
      } else {
        const itemStr = this.storageClient.getItem(key);
        if (!itemStr) {
          return undefined;
        }
        return itemStr;
      }
    } catch (error) {
      console.error(`Error loading value from query or sessionStorage: ${error}`);
      return undefined;
    }
  }
  addBifrostOverride(url) {
    try {
      var _this$plugin, _this$plugin2;
      if (!((_this$plugin = this.plugin) !== null && _this$plugin !== void 0 && _this$plugin.bifrostOverrideKey)) {
        return url;
      }
      let bifrostOverrideValue = this.loadFromQueryAndSaveToSession((_this$plugin2 = this.plugin) === null || _this$plugin2 === void 0 ? void 0 : _this$plugin2.bifrostOverrideKey);
      if (!bifrostOverrideValue) {
        return url;
      }
      const urlToUpdate = new URL(url);
      if (urlToUpdate.protocol === 'https:' || urlToUpdate.protocol === 'http:') {
        let inputParams = new URLSearchParams(urlToUpdate.search);
        inputParams.set('bifrost-override', bifrostOverrideValue);
        urlToUpdate.search = inputParams.toString();
      }
      return urlToUpdate.toString();
    } catch (e) {
      console.error('Error adding bifrost override to url. Falling back to original url.');
      return url;
    }
  }

  /**
   * If a moduleUriMapper is defined on the plugin, then we call it passing through the hash function.
   * Otherwise return the moduleUri defined in the import map
   *
   * @param importMap
   */
  getModuleUri(importMap = this.importMap) {
    if (importMap) {
      var _this$plugin3, _this$plugin4;
      let moduleUri = importMap.moduleUri;
      if ((_this$plugin3 = this.plugin) !== null && _this$plugin3 !== void 0 && _this$plugin3.moduleUriMapper) {
        moduleUri = this.plugin.moduleUriMapper(importMap.moduleUri, this.variant, generateHash);
      }
      if ((_this$plugin4 = this.plugin) !== null && _this$plugin4 !== void 0 && _this$plugin4.bifrostOverrideKey) {
        moduleUri = this.addBifrostOverride(moduleUri);
      }
      return moduleUri;
    }
    return '';
  }

  /**
   * Returns the module if it has been resolved into the internal module map
   */
  getResolvedModule() {
    return this.resolvedModuleMap.get(this.getModuleName());
  }

  /**
   * Returns whether the module has been registered by SystemJS AND the current SDK
   */
  isModuleRegisteredAndValid() {
    // a module can appear in the registry as `null` if it fails to load
    return System.has(this.getModuleUri()) && !!System.get(this.getModuleUri()) && !!this.getResolvedModule();
  }

  /**
   * Set up our Import Maps dynamically from the provided import config.
   * We also set the plugin.externals into the module registry so that they are shared.
   * However, these are scoped only to our remote import.
   *
   * https://github.com/systemjs/systemjs/blob/main/docs/import-maps.md
   *
   * @param importMaps
   */
  setImportMaps(importMaps) {
    let scopedModules = null;
    try {
      var _this$plugin5;
      // Set the external packages into the System registry using the locally resolved (from the Product) packages
      scopedModules = setupSystemModules(importMaps.flatMap(item => item.externals), ((_this$plugin5 = this.plugin) === null || _this$plugin5 === void 0 ? void 0 : _this$plugin5.name) || PluginName.AppSwitcher);
    } catch (e) {
      // TODO add logger
      console.error(e);
    }
    System.addImportMap(importMaps.reduce((acc, item) => {
      const moduleUri = this.getModuleUri(item);
      acc.imports[item.moduleName] = moduleUri;
      if (scopedModules && item.externals.length) {
        acc.scopes[moduleUri] = {};
        for (const external of item.externals) {
          const externalModuleIdentifier = 'name' in external ? external.name : external.module;
          acc.scopes[moduleUri][externalModuleIdentifier] = scopedModules[externalModuleIdentifier];
        }
      }
      return acc;
    }, {
      imports: {},
      scopes: {}
    }));
  }

  /**
   * Fire off the request to fetch our module from the System.JS import map.
   * It is then stored in the resolved module map for easy use later.
   */
  async fetchModule() {
    if (this.importMap) {
      const {
        moduleName
      } = this.importMap;
      const module = await remoteImport(moduleName);
      this.resolvedModuleMap.set(moduleName, module);
      return module;
    }
  }
  resolveExport(exportName) {
    const resolvedModule = this.getResolvedModule();
    if (resolvedModule) {
      if (exportName) {
        return resolvedModule[exportName];
      }
      return resolvedModule;
    }
  }

  /**
   * Render our resolved module as a React component in the existing React Tree.
   * This DOES NOT create a new root.
   *
   * @param exportName
   * @param props
   * @param fallback
   */
  renderComponent(exportName = 'default', props, fallback = null, children = null, experience = null, onEvent = NO_OP) {
    const component = this.resolveExport(exportName);
    if (component) {
      let componentToRender;
      if ( /*#__PURE__*/React.isValidElement(component)) {
        componentToRender = props ? /*#__PURE__*/React.cloneElement(component, props, children) : component;
      } else {
        componentToRender = /*#__PURE__*/React.createElement(component, props, children);
      }
      onEvent({
        type: 'render-complete'
      });
      experience === null || experience === void 0 ? void 0 : experience.success({
        metadata: {
          moduleName: this.getModuleName()
        }
      });
      return componentToRender;
    }
    return fallback;
  }
}
export default AtlassianSystemRemoteFrontendSdk;